import { useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, Home, X } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useDemo } from "@/store/DemoStore";
import { Avatar, btn, normalizePhone, normalizePhoneOnBlur } from "@/components/common";
import { cn } from "@/lib/utils";

const field = "h-11 w-full rounded-xl border border-slate-200 bg-white px-3.5 text-base text-navy-900 outline-none focus:border-navy-500 focus:ring-2 focus:ring-navy-100 sm:text-sm";

export function AddMemberDialog({ head, open, onOpenChange }) {
  const { clients, addHouseholdMember } = useDemo();
  const [mode, setMode] = useState("existing");
  const [sel, setSel] = useState("");
  const [f, setF] = useState({ name: "", email: "", phone: "" });
  const available = clients.filter((c) => !c.householdId && c.id !== head.id);
  const submit = async (e) => {
    e.preventDefault();
    const r = await addHouseholdMember(head.id, mode === "existing" ? { clientId: sel } : f);
    if (r) { onOpenChange(false); setSel(""); setF({ name: "", email: "", phone: "" }); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-1.5rem)] max-w-md rounded-2xl p-5 sm:p-7" data-testid="add-member-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-navy-900">Add to {head.name}'s Household</DialogTitle>
          <DialogDescription>Link an existing client or create a new person to add as a household member.</DialogDescription>
        </DialogHeader>
        <div className="mb-4 inline-flex gap-1 rounded-xl bg-slate-100 p-1">
          {[["existing", "Existing client"], ["new", "New person"]].map(([v, l]) => (
            <button key={v} type="button" onClick={() => setMode(v)} data-testid={`add-member-mode-${v}`} className={cn("rounded-lg px-3.5 py-1.5 text-sm font-medium transition-colors", mode === v ? "bg-white text-navy-900 shadow-sm" : "text-slate-500 hover:text-navy-900")}>{l}</button>
          ))}
        </div>
        <form onSubmit={submit} className="space-y-3">
          {mode === "existing" ? (
            available.length ? (
              <select required value={sel} onChange={(e) => setSel(e.target.value)} className={field} data-testid="add-member-existing-select">
                <option value="" disabled>Select a client…</option>
                {available.map((c) => <option key={c.id} value={c.id}>{c.name} · {c.email}</option>)}
              </select>
            ) : <p className="rounded-xl bg-slate-50 p-4 text-sm text-slate-500">No unassigned clients available. Add a new person instead.</p>
          ) : (
            <>
              <input required placeholder="Full name" className={field} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="add-member-name" />
              <input required type="email" placeholder="Email" className={field} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="add-member-email" />
              <input type="tel" placeholder="Phone (optional)" className={field} value={f.phone} onChange={(e) => setF({ ...f, phone: normalizePhone(e.target.value) })} onBlur={normalizePhoneOnBlur((n) => setF((p) => ({ ...p, phone: n })))} data-testid="add-member-phone" />
            </>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={() => onOpenChange(false)} className={btn.secondary}>Cancel</button>
            <button className={btn.primary} data-testid="add-member-submit" disabled={mode === "existing" && !sel}>Add Member</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function MemberRow({ m }) {
  const { makeHouseholdPrimary, removeHouseholdMember } = useDemo();
  return (
    <div className="flex items-center gap-3 rounded-xl bg-white px-3 py-2.5 ring-1 ring-slate-100" data-testid={`household-member-${m.id}`}>
      <Avatar name={m.name} size="sm" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-navy-900">{m.name}</p>
        <p className="truncate text-xs text-slate-500">{m.email || "—"}</p>
      </div>
      <button onClick={() => makeHouseholdPrimary(m.id)} data-testid={`make-primary-${m.id}`} className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-navy-900 transition-colors hover:bg-slate-50"><Home className="h-3.5 w-3.5" />Make Primary</button>
      <Link to={`/agent/clients/${m.id}`} data-testid={`member-open-${m.id}`} className="grid h-8 w-8 place-items-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-navy-700"><ExternalLink className="h-4 w-4" /></Link>
      <button onClick={() => removeHouseholdMember(m.id)} data-testid={`remove-member-${m.id}`} className="grid h-8 w-8 place-items-center rounded-lg text-slate-400 transition-colors hover:bg-red-50 hover:text-red-500"><X className="h-4 w-4" /></button>
    </div>
  );
}
